
import { cn } from "@/lib/utils";
import { DealerRating } from "./DealerRating";
import { DealerPriceSection } from "./DealerPriceSection";
import { DealerCardActions } from "./DealerCardActions";
import { useStore } from "@/store/useStore";
import { useDealerPrice } from "./useDealerPrice";
import { DEFAULT_GOLD_PURITY } from "@/lib/constants";

interface DealerCardContentProps {
  dealerBuyPrice?: number; // Make optional since we calculate it ourselves
  marketPrice?: number; // Make optional since we calculate it ourselves
  buySpread?: number;
  rating: number;
  rating_count?: number; // Add rating_count prop
  email?: string;
  distance?: string;
  is_verified: boolean;
  activated: boolean;
  dealerId: string;
  city?: string;
  dealerName?: string;
  dealerAddress?: string;
  onActivateClick?: () => void;
  onReportClick?: () => void;
  isHomePage?: boolean;
  isHorizontalLayout?: boolean;
  metalPurity?: string;
  metal?: "gold" | "silver";
}

export function DealerCardContent({
  buySpread,
  rating,
  rating_count = 0, // Add rating_count with default value
  email,
  distance,
  is_verified,
  activated,
  dealerId,
  city,
  dealerName,
  dealerAddress,
  onActivateClick,
  onReportClick,
  isHomePage = false,
  isHorizontalLayout = false,
  metalPurity,
  metal = "gold"
}: DealerCardContentProps) {
  // Get the selected purity from PriceStore with fallback to defaults
  const { goldPurity, currentGoldPrice: rawGoldPrice } = useStore();
  const effectiveGoldPurity = goldPurity || DEFAULT_GOLD_PURITY;
  
  // Calculate prices based on the selected purity
  // Note: We pass buySpread directly as it's already negative in the DB
  const { marketPrice, dealerBuyPrice } = useDealerPrice(rawGoldPrice, effectiveGoldPurity, activated ? buySpread : undefined);
  
  // Log values for debugging
  console.log(`DealerCardContent - Calculating prices: marketPrice=${marketPrice}, dealerBuyPrice=${dealerBuyPrice}, buySpread=${buySpread}, activated=${activated}`);
  
  return (
    <div className={cn(
      "flex-1 p-4 flex flex-col justify-between relative",
      isHorizontalLayout && "md:border-l border-metal-light/30 md:p-5"
    )}>
      <div className="flex flex-col gap-4">
        <DealerPriceSection
          dealerBuyPrice={dealerBuyPrice}
          marketPrice={marketPrice}
          buySpread={buySpread}
          distance={distance}
          activated={activated === true}
          isHomePage={isHomePage}
          metalPurity={metalPurity}
          metal={metal}
        />
        
        <div className="flex items-center">
          <DealerRating rating={rating} rating_count={rating_count} />
        </div>
      </div>
      
      <div className="mt-auto">
        <DealerCardActions
          activated={activated}
          dealerId={dealerId}
          city={city}
          dealerName={dealerName}
          dealerAddress={dealerAddress}
          onActivateClick={onActivateClick}
          onReportClick={onReportClick}
          isHomePage={isHomePage}
        />
      </div>
    </div>
  );
}
